import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Avatar,
  Rating,
  IconButton,
  TextField,
  useMediaQuery,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';

const ReviewHistory = ({ resellerId }) => {
  const [reviews, setReviews] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({ price: '', description: '' });

  const isMobile = useMediaQuery('(max-width: 768px)');

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`https://reviewhub-lc.onrender.com/api/reviews/reseller/${resellerId}`);
      setReviews(res.data);
    } catch (err) {
      console.error('Error fetching reviews:', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this review?')) {
      try {
        await axios.delete(`https://reviewhub-lc.onrender.com/api/reviews/${id}`);
        fetchReviews();
      } catch (error) {
        console.error('Failed to delete review:', error);
      }
    }
  };

  const handleEdit = (review) => {
    setEditId(review._id);
    setEditData({ price: review.price, description: review.description });
  };

  const handleCancel = () => {
    setEditId(null);
    setEditData({ price: '', description: '' });
  };

  const handleSave = async (id) => {
    try {
      await axios.patch(`https://reviewhub-lc.onrender.com/api/reviews/reviews/${id}`, {
        price: editData.price,
        description: editData.description,
      });
      alert('Review updated successfully ✅');
      fetchReviews();
      handleCancel();
    } catch (err) {
      console.error('Failed to update review:', err);
      alert('❌ Failed to update review. Please try again.');
    }
  };

  useEffect(() => {
    if (resellerId) {
      fetchReviews();
    }
  }, [resellerId]);

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>
        My Review History
      </Typography>

      {isMobile ? (
        // Mobile Card Layout
        <Box display="flex" flexDirection="column" gap={2}>
          {reviews.map((review) => (
            <Paper key={review._id} sx={{ p: 2 }}>
              <Box display="flex" gap={2} alignItems="flex-start">
                <Avatar
                  variant="rounded"
                  src={`https://reviewhub-lc.onrender.com/${review.productMainImage}`}
                  alt={review.productName}
                  sx={{ width: 64, height: 64 }}
                />
                <Box flexGrow={1}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {review.productName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {review.category} • {review.subCategory}
                  </Typography>
                  <Typography variant="body2">Platform: {review.platform}</Typography>
                  <Typography variant="body2">Gender: {review.gender}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    Price: ₹
                    {editId === review._id ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editData.price}
                        onChange={(e) =>
                          setEditData({ ...editData, price: e.target.value })
                        }
                        sx={{ width: 80, ml: 1 }}
                      />
                    ) : (
                      review.price
                    )}
                  </Typography>
                  <Rating value={review.rating} readOnly size="small" />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Description:
                    {editId === review._id ? (
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        value={editData.description}
                        onChange={(e) =>
                          setEditData({ ...editData, description: e.target.value })
                        }
                      />
                    ) : (
                      review.description
                    )}
                  </Typography>
                  <Box mt={1}>
                    {editId === review._id ? (
                      <>
                        <IconButton color="success" onClick={() => handleSave(review._id)}>
                          <SaveIcon />
                        </IconButton>
                        <IconButton color="warning" onClick={handleCancel}>
                          <CancelIcon />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton color="primary" onClick={() => handleEdit(review)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleDelete(review._id)}>
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            </Paper>
          ))}
          {reviews.length === 0 && (
            <Typography align="center" color="text.secondary">
              No reviews found.
            </Typography>
          )}
        </Box>
      ) : (
        // Desktop Table Layout
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell>Image</TableCell>
                <TableCell>Product Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>SubCategory</TableCell>
                <TableCell>Platform</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Color</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reviews.map((review) => (
                <TableRow key={review._id}>
                  <TableCell>
                    <Avatar
                      variant="rounded"
                      src={`https://reviewhub-lc.onrender.com/${review.productMainImage}`}
                      alt={review.productName}
                      sx={{ width: 56, height: 56 }}
                    />
                  </TableCell>
                  <TableCell>{review.productName}</TableCell>
                  <TableCell>{review.category}</TableCell>
                  <TableCell>{review.subCategory}</TableCell>
                  <TableCell>{review.platform}</TableCell>
                  <TableCell>{review.gender}</TableCell>
                  <TableCell>
                    {editId === review._id ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editData.price}
                        onChange={(e) =>
                          setEditData({ ...editData, price: e.target.value })
                        }
                      />
                    ) : (
                      `₹${review.price}`
                    )}
                  </TableCell>
                  <TableCell>{review.color}</TableCell>
                  <TableCell>
                    <Rating value={review.rating} readOnly size="small" />
                  </TableCell>
                  <TableCell>
                    {editId === review._id ? (
                      <TextField
                        size="small"
                        multiline
                        value={editData.description}
                        onChange={(e) =>
                          setEditData({ ...editData, description: e.target.value })
                        }
                      />
                    ) : (
                      review.description
                    )}
                  </TableCell>
                  <TableCell>
                    {editId === review._id ? (
                      <>
                        <IconButton color="success" onClick={() => handleSave(review._id)}>
                          <SaveIcon />
                        </IconButton>
                        <IconButton color="warning" onClick={handleCancel}>
                          <CancelIcon />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton color="primary" onClick={() => handleEdit(review)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleDelete(review._id)}>
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {reviews.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    No reviews found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ReviewHistory;
